import { useEffect, useMemo, useState } from 'react'

import { WEBSITE_PROMO_EXCLUDED_ORG_IDS } from '../data/websitePromoExcludedOrgs'

import { WebsitePromoState } from '@/modules/users/types/User'
import { useAuth } from '@/hooks/useAuth'
import { customPost } from '@/utils/customFetch'
import { makeUrlUserWebsitePromo } from '@/hooks/connectionData'

type PromoAction = 'dismiss' | 'convert'

const isShowable = (promo: WebsitePromoState | undefined): boolean => {
  if (!promo || promo.stage === 0) return true

  if (promo.stage >= 3) return false

  return Boolean(promo.nextShowAt) && new Date(promo.nextShowAt as string).getTime() <= Date.now()
}

export const useWebsitePromo = () => {
  const { userData, setUserData } = useAuth()
  const organizationId = userData?.organization?._id
  const userId = userData?.user?._id
  // La exclusión es por organización (ya tiene página Veekls); el recorrido de
  // descartes es por persona — no se contempla el caso de un usuario con
  // perfiles en más de una organización, es un caso interno, no el flujo normal.
  const excluded = Boolean(organizationId && WEBSITE_PROMO_EXCLUDED_ORG_IDS.has(organizationId))

  const shouldShow = useMemo(
    () => Boolean(userId) && !excluded && isShowable(userData?.user?.promos?.websitePromo),
    [userId, excluded, userData?.user?.promos?.websitePromo]
  )

  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (shouldShow) setOpen(true)
  }, [shouldShow])

  const sendAction = async (action: PromoAction) => {
    if (!userId) return

    try {
      const { websitePromo } = await customPost<{ websitePromo: WebsitePromoState }>(makeUrlUserWebsitePromo(), {
        userId,
        action,
      })

      setUserData((previous) =>
        previous?.user ? { ...previous, user: { ...previous.user, promos: { websitePromo } } } : previous
      )
    } catch (error) {
      console.error('Error updating website promo state:', error)
    } finally {
      setOpen(false)
    }
  }

  return {
    open,
    dismiss: () => sendAction('dismiss'),
    convert: () => sendAction('convert'),
  }
}
